import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Alert,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Image as ImageIcon,
  Videocam as VideoIcon,
  BarChart as InfographicIcon,
  Audiotrack as AudioIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';

function Step9MediaPreferences({ mediaPreferences, onChange }) {
  const [selectedMedia, setSelectedMedia] = useState(
    Array.isArray(mediaPreferences) ? mediaPreferences : []
  );
  const [showInfo, setShowInfo] = useState(true);

  const mediaOptions = [
    {
      id: 'images',
      label: 'Images',
      icon: <ImageIcon />,
      description: 'Photos, illustrations, and graphics to enhance your content'
    },
    {
      id: 'videos',
      label: 'Videos',
      icon: <VideoIcon />,
      description: 'Embedded videos and tutorials for visual learning'
    },
    {
      id: 'infographics',
      label: 'Infographics',
      icon: <InfographicIcon />,
      description: 'Data visualizations and informative graphics'
    },
    {
      id: 'audio',
      label: 'Audio',
      icon: <AudioIcon />,
      description: 'Podcasts, sound clips, and audio content'
    }
  ];

  const handleMediaToggle = (mediaId) => {
    let updatedMedia;
    if (selectedMedia.includes(mediaId)) {
      updatedMedia = selectedMedia.filter(id => id !== mediaId);
    } else {
      updatedMedia = [...selectedMedia, mediaId];
    }
    setSelectedMedia(updatedMedia);
    onChange(updatedMedia);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Step 9: Media Preferences
      </Typography>

      <Collapse in={showInfo}>
        <Alert 
          severity="info"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setShowInfo(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          Select the types of media you'd like to include in your blog posts
        </Alert>
      </Collapse>

      {/* Main Selection Area */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          {mediaOptions.map((option) => (
            <Box key={option.id}>
              <Chip
                icon={option.icon}
                label={option.label}
                onClick={() => handleMediaToggle(option.id)}
                variant={selectedMedia.includes(option.id) ? "filled" : "outlined"}
                color={selectedMedia.includes(option.id) ? "primary" : "default"}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  height: 'auto',
                  py: 1,
                  '& .MuiChip-label': {
                    display: 'flex',
                    flexDirection: 'column',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pl: 2, mt: 0.5, display: 'block' }}>
                {option.description}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Tips Section */}
      <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon fontSize="small" />
          Tips for Media Usage:
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label="Optimize file sizes" />
          <Chip size="small" label="Use relevant media" />
          <Chip size="small" label="Credit sources" />
          <Chip size="small" label="Maintain consistency" />
        </Stack>
      </Paper>
    </Box>
  );
}

export default Step9MediaPreferences;
